// Shared building blocks of the Voice of Dubai player screens.
import React, { useEffect, useRef, useState } from 'react';
import { getLang, setLang, t, useLang } from '../../i18n';
import { publicGetLanguages } from '../../api';

// ---------- icons ----------
const sv = (path, { size = 20, color = '#0F1A3A', sw = 1.6, cls } = {}) => (
  <svg className={cls} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: path }} />
);
export const Icon = {
  back: (o) => sv('<path d="M15 6l-6 6 6 6"/>', { size: 22, sw: 2, ...o }),
  arrow: (o) => sv('<path d="M4 12h16M14 6l6 6-6 6"/>', { size: 22, color: '#fff', sw: 1.8, cls: 'arrow', ...o }),
  arrowLeft: (o) => sv('<path d="M20 12H4M10 6l-6 6 6 6"/>', { size: 22, sw: 1.8, cls: 'arrow', ...o }),
  chevron: (o) => sv('<path d="M9 6l6 6-6 6"/>', { size: 18, color: '#fff', sw: 2, cls: 'arrow', ...o }),
  down: (o) => sv('<path d="M6 9l6 6 6-6"/>', { size: 14, sw: 2.2, ...o }),
  globe: (o) => sv('<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z"/>', { size: 18, ...o }),
  pin: (o) => sv('<path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.5"/>', { size: 19, ...o }),
  headphones: (o) => sv('<path d="M4 15v-3a8 8 0 0 1 16 0v3"/><rect x="3" y="14" width="4" height="7" rx="1.5"/><rect x="17" y="14" width="4" height="7" rx="1.5"/>', { size: 19, ...o }),
  clock: (o) => sv('<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>', { size: 19, ...o }),
  map: (o) => sv('<path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3z"/><path d="M9 3v15M15 6v15"/>', { size: 20, ...o }),
  walk: (o) => sv('<circle cx="13" cy="4" r="2"/><path d="M10 21l2-6-3-3 1-5 4 3 3 1M9 12l-2 4"/>', { size: 19, ...o }),
  user: (o) => sv('<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/>', { size: 20, color: '#4A5068', ...o }),
  mail: (o) => sv('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>', { size: 20, color: '#4A5068', ...o }),
  lock: (o) => sv('<rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/><circle cx="12" cy="15.5" r="1.2"/>', { size: 20, color: '#4A5068', ...o }),
  eye: (o) => sv('<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>', { size: 22, color: '#4A5068', ...o }),
  eyeOff: (o) => sv('<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/><path d="M3 3l18 18"/>', { size: 22, color: '#4A5068', ...o }),
  menu: (o) => sv('<path d="M4 7h16M4 12h16M4 17h16"/>', { size: 22, sw: 1.8, ...o }),
  film: (o) => sv('<rect x="3" y="4" width="18" height="16" rx="1.5"/><path d="M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4"/>', { size: 24, color: '#B8893A', sw: 1.5, ...o }),
  bulb: (o) => sv('<path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.8.8 1 1.5 1 2.5h6c0-1 .2-1.7 1-2.5A6 6 0 0 0 12 3z"/>', { size: 22, color: '#B8893A', ...o }),
};

// ---------- logo tile ----------
export function LogoTile({ size = 'md' }) {
  return (
    <div className={`vod-logo ${size}`}>
      <img src="/emblem.png" alt="" />
      <span>VOICE <b>OF</b> DUBAI</span>
    </div>
  );
}

// ---------- language switcher (pill + menu) ----------
let langsCache = null;
export function useContentLanguages() {
  const [list, setList] = useState(langsCache || []);
  useEffect(() => {
    if (langsCache) return;
    publicGetLanguages().then(l => { langsCache = l; setList(l); }).catch(() => {});
  }, []);
  return list;
}

export function LangSwitcher({ withGlobe = true }) {
  const lang = useLang();
  const list = useContentLanguages();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  if (list.length < 2) return null;
  return (
    <div className="vod-lang" ref={ref}>
      <button type="button" className="vod-round" onClick={() => setOpen(o => !o)} aria-label={t('language')}>
        {withGlobe && Icon.globe()}
        {lang.toUpperCase()}
        {Icon.down()}
      </button>
      {open && (
        <div className="vod-lang-menu">
          {list.map(l => (
            <button key={l.code} type="button" className={l.code === lang ? 'on' : ''}
              onClick={() => { setOpen(false); if (l.code !== getLang()) setLang(l.code); }}>
              <span dir="auto">{l.native_name || l.name}</span><small>{l.code.toUpperCase()}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- segmented progress ----------
export function SegProgress({ total, current }) {
  return (
    <div className="vod-seg">
      {Array.from({ length: total }, (_, i) => (
        <i key={i} className={i + 1 < current ? 'done' : i + 1 === current ? 'now' : ''} />
      ))}
    </div>
  );
}

// ---------- toast ----------
export function useToast() {
  const [msg, setMsg] = useState('');
  const [show, setShow] = useState(false);
  const timer = useRef(null);
  const toast = (text) => {
    setMsg(text); setShow(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setShow(false), 2200);
  };
  const node = <div className={`vod-toast ${show ? 'show' : ''}`}>{msg}</div>;
  return [toast, node];
}

// ---------- route helpers ----------
function haversineKm(a, b) {
  const R = 6371, rad = (d) => d * Math.PI / 180;
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

// points: [{lat, lng, title}] in walking order
export function routeStats(points) {
  let km = 0;
  for (let i = 1; i < points.length; i++) km += haversineKm(points[i - 1], points[i]);
  km *= 1.3; // streets are not straight lines
  const min = Math.max(1, Math.round(km / 4.5 * 60)); // ~4.5 km/h walking pace
  return { km: km < 10 ? km.toFixed(1) : Math.round(km), min, stops: points.length };
}

const ll = (p) => `${p.lat},${p.lng}`;

// Google Maps walking route, embedded without an API key
export function googleEmbedUrl(points, lang) {
  if (!points.length) return '';
  if (points.length === 1) return `https://maps.google.com/maps?q=${ll(points[0])}&z=16&hl=${lang}&output=embed`;
  const [first, ...rest] = points;
  return `https://maps.google.com/maps?saddr=${ll(first)}&daddr=${rest.map(ll).join('+to:')}&dirflg=w&hl=${lang}&output=embed`;
}

// Opens the route in the Google Maps app / site
export function googleDirectionsUrl(points) {
  if (!points.length) return '';
  if (points.length === 1) return `https://www.google.com/maps/dir/?api=1&destination=${ll(points[0])}&travelmode=walking`;
  const first = points[0], last = points[points.length - 1];
  const mid = points.slice(1, -1).slice(0, 8).map(ll).join('|');
  return `https://www.google.com/maps/dir/?api=1&origin=${ll(first)}&destination=${ll(last)}${mid ? `&waypoints=${encodeURIComponent(mid)}` : ''}&travelmode=walking`;
}

// Player's current position (asked only when `active` becomes true). status: idle | asking | ok | denied | unavailable
export function useMyPosition(active) {
  const [pos, setPos] = useState(null);
  const [status, setStatus] = useState('idle');
  const ask = React.useCallback(() => {
    if (!navigator.geolocation) { setStatus('unavailable'); return; }
    setStatus('asking');
    navigator.geolocation.getCurrentPosition(
      p => { setPos({ lat: +p.coords.latitude.toFixed(6), lng: +p.coords.longitude.toFixed(6), title: 'me' }); setStatus('ok'); },
      err => setStatus(err && err.code === 1 ? 'denied' : 'unavailable'),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  }, []);
  useEffect(() => { if (active && status === 'idle') ask(); }, [active, status, ask]);
  return { pos, status, refresh: ask };
}

export function RouteMap({ points, height = 220 }) {
  const lang = useLang();
  if (!points || points.length === 0) return null;
  return (
    <iframe title="map" src={googleEmbedUrl(points, lang)} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
      style={{ width: '100%', height, border: 0, borderRadius: 16, display: 'block', background: '#eee' }} />
  );
}
